/**
 * Core API Client
 * Handles all HTTP requests with retry logic, error handling, and caching
 */

import { API_CONFIG, buildQueryString, getApiUrl } from './api.config';
import { ApiClientResponse, ApiError, RequestOptions } from './api.types';
import { ApiException, handleApiError, isNetworkError } from './api.errors';

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Create AbortController with timeout
 */
const createAbortController = (timeout: number) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  return {
    controller,
    cleanup: () => clearTimeout(timeoutId),
  };
};

/**
 * Core fetch wrapper with retry logic
 */
async function fetchWithRetry(
  url: string,
  options: RequestOptions = {},
  attempt = 1
): Promise<Response> {
  const {
    timeout = API_CONFIG.TIMEOUT,
    retries = API_CONFIG.RETRY.MAX_ATTEMPTS,
    ...fetchOptions
  } = options;

  const { controller, cleanup } = createAbortController(timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    cleanup();
    return response;
  } catch (error) {
    cleanup();

    // Don't retry if manually aborted or max attempts reached
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiException('Request timeout', 408);
    }

    if (attempt >= retries || !isNetworkError(error)) {
      throw error;
    }

    // Calculate delay with exponential backoff
    const delay = API_CONFIG.RETRY.DELAY * Math.pow(API_CONFIG.RETRY.BACKOFF_MULTIPLIER, attempt - 1);
    await sleep(delay);

    return fetchWithRetry(url, options, attempt + 1);
  }
}

/**
 * Process API response
 */
async function processResponse<T>(response: Response): Promise<ApiClientResponse<T>> {
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  if (!response.ok) {
    const error: ApiError = isJson
      ? await response.json()
      : { status: response.status, message: response.statusText };
    
    return {
      success: false,
      error: {
        ...error,
        status: response.status,
      },
    };
  }

  const data = isJson ? await response.json() : await response.text();

  // Handle paginated responses
  if (data?.docs !== undefined) {
    return {
      success: true,
      data: data.docs,
      meta: {
        totalDocs: data.totalDocs,
        totalPages: data.totalPages,
        page: data.page,
        limit: data.limit,
        hasNextPage: data.hasNextPage,
        hasPrevPage: data.hasPrevPage,
        nextPage: data.nextPage,
        prevPage: data.prevPage,
      },
    };
  }

  return {
    success: true,
    data,
  };
}

/**
 * API Client class
 */
export class ApiClient {
  private baseUrl: string;
  private defaultHeaders: HeadersInit;
  private token?: string;

  constructor(baseUrl?: string, token?: string) {
    this.baseUrl = baseUrl || API_CONFIG.BASE_URL;
    this.defaultHeaders = { ...API_CONFIG.DEFAULT_HEADERS };
    this.token = token;
  }

  /**
   * Set authentication token
   */
  setToken(token: string | undefined) {
    this.token = token;
  }

  /**
   * Get authentication token
   */
  getToken(): string | undefined {
    return this.token;
  }

  /**
   * Build headers for request
   */
  private buildHeaders(options: RequestOptions = {}): HeadersInit {
    const headers: HeadersInit = {
      ...this.defaultHeaders,
      ...options.headers,
    };

    const token = options.token || this.token;
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `JWT ${token}`;
    }

    return headers;
  }

  /**
   * Build full URL with query params
   */
  private buildUrl(endpoint: string, params?: Record<string, any>): string {
    const url = endpoint.startsWith('http') ? endpoint : getApiUrl(endpoint);
    
    if (params && Object.keys(params).length > 0) {
      const queryString = buildQueryString(params);
      return queryString ? `${url}?${queryString}` : url;
    }

    return url;
  }

  /**
   * Generic request handler
   */
  private async _request<T = any>(
    endpoint: string,
    options: RequestOptions = {},
    data?: any
  ): Promise<ApiClientResponse<T>> {
    try {
      const url = this.buildUrl(endpoint, options.params);
      const headers = this.buildHeaders(options);

      const requestOptions: RequestOptions = {
        ...options,
        headers,
      };
      
      if (data) {
        requestOptions.body = JSON.stringify(data);
      }

      // Add Next.js cache options if available
      if (options.revalidate !== undefined) {
        (requestOptions as any).next = { revalidate: options.revalidate };
      }

      const response = await fetchWithRetry(url, requestOptions);
      return processResponse<T>(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * GET request
   */
  async get<T = any>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this._request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T = any>(
    endpoint: string,
    data?: any,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this._request<T>(endpoint, { ...options, method: 'POST' }, data);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    endpoint: string,
    data?: any,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this._request<T>(endpoint, { ...options, method: 'PATCH' }, data);
  }

  /**
   * PUT request
   */
  async put<T = any>(
    endpoint: string,
    data?: any,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this._request<T>(endpoint, { ...options, method: 'PUT' }, data);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this._request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Upload file
   */
  async upload<T = any>(
    endpoint: string,
    file: File | Blob,
    data?: Record<string, any>,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    try {
      const url = this.buildUrl(endpoint, options.params);
      const formData = new FormData();
      
      // Add file
      formData.append('file', file);
      
      // Add additional data
      if (data) {
        Object.entries(data).forEach(([key, value]) => {
          formData.append(key, String(value));
        });
      }

      // Don't set Content-Type for FormData - browser will set it with boundary
      const headers = this.buildHeaders(options);
      delete (headers as Record<string, string>)['Content-Type'];

      const response = await fetchWithRetry(url, {
        ...options,
        method: 'POST',
        headers,
        body: formData,
      });

      return processResponse<T>(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export convenience methods
export const api = {
  get: <T = any>(endpoint: string, options?: RequestOptions) => 
    apiClient.get<T>(endpoint, options),
  
  post: <T = any>(endpoint: string, data?: any, options?: RequestOptions) => 
    apiClient.post<T>(endpoint, data, options),
  
  patch: <T = any>(endpoint: string, data?: any, options?: RequestOptions) => 
    apiClient.patch<T>(endpoint, data, options),
  
  put: <T = any>(endpoint: string, data?: any, options?: RequestOptions) => 
    apiClient.put<T>(endpoint, data, options),
  
  delete: <T = any>(endpoint: string, options?: RequestOptions) => 
    apiClient.delete<T>(endpoint, options),
  
  upload: <T = any>(endpoint: string, file: File | Blob, data?: Record<string, any>, options?: RequestOptions) => 
    apiClient.upload<T>(endpoint, file, data, options),
  
  setToken: (token: string | undefined) => apiClient.setToken(token),
  getToken: () => apiClient.getToken(),
};
